import { createStore } from 'vuex';
import io from 'socket.io-client';
import { getSocketUrl } from '../utils/serverUrl';

function getToken() {
  return localStorage.getItem('token') || '';
}

const socket = io(getSocketUrl(), {
  auth: () => ({ token: getToken() }),
});

const waitingAudio = new Audio('/audio/waiting.mp3');
const startingAudio = new Audio('/audio/start.mp3');
let lastAudioRound = null;
let roundAudioDelayTimer = null;

waitingAudio.preload = 'auto';
waitingAudio.loop = false;
startingAudio.preload = 'auto';
startingAudio.loop = false;

function stopRoundAudio() {
  waitingAudio.pause();
  startingAudio.pause();
  waitingAudio.currentTime = 0;
  startingAudio.currentTime = 0;
}

function playRoundAudioSequence(round) {
  const normalizedRound = Number(round);
  if (
    !Number.isInteger(normalizedRound) ||
    normalizedRound <= 0 ||
    lastAudioRound === normalizedRound
  ) {
    return;
  }

  lastAudioRound = normalizedRound;
  clearTimeout(roundAudioDelayTimer);
  stopRoundAudio();
  roundAudioDelayTimer = setTimeout(() => {
    waitingAudio.onended = () => {
      waitingAudio.onended = null;
      startingAudio.currentTime = 0;
      startingAudio.play().catch(() => {});
    };
    waitingAudio.play().catch(() => {
      waitingAudio.onended = null;
    });
  }, 19000);
}

export default createStore({
  state: {
    hasPoint: 1000,
    users: null,
    messages: [],
    socket,
    gameResults: [], // {round,value,parity,side,created_at}
    resultProcessing: null,
    realtimeListenersBound: false,
    currentUser: null,
  },
  getters: {},
  mutations: {
    bet(state) {
      Math.floor(Math.random() * 2) == 0 ? (state.hasPoint *= 2) : (state.hasPoint /= 2);
      // console.log(state.hasPoint);
    },
    setUsers(state, users) {
      state.users = users;
      if (users) {
        state.currentUser = {
          ...users,
          _id: users._id || users.id,
        };
      }
    },
    sendMessage(state, message) {
      if (!state.currentUser) {
        return;
      }
      state.socket.auth = { token: getToken() };

      const messageData = {
        message: message,
        token: getToken(),
      };

      state.socket.emit('chat message', messageData);
      // 임시 push 코드 삭제 완료
    },
    onMessage(state) {
      if (state.realtimeListenersBound) return;
      state.realtimeListenersBound = true;

      state.socket.on('chat message', (msg) => {
        // 중복 메시지 방지
        const isDuplicate = state.messages.some((m) => m.id === msg.id);
        if (!isDuplicate) {
          state.messages.push(msg);
        }
      });
      state.socket.on('game result pending', (processing) => {
        state.resultProcessing = processing;
      });
      // 서버에서 전송되는 게임 결과
      state.socket.on('game result', (res) => {
        const parity = res.parity || (res.value % 2 === 0 ? '짝' : '홀');
        const side = res.value <= 4 ? '언더' : '오버';
        const normalized = { ...res, parity, side };
        const existingIndex = state.gameResults.findIndex(
          (item) => Number(item.round) === Number(normalized.round),
        );
        if (existingIndex >= 0) {
          state.gameResults.splice(existingIndex, 1);
        }
        state.gameResults.unshift(normalized);
        if (state.gameResults.length > 100) {
          state.gameResults.pop();
        }
        if (Number(state.resultProcessing?.round) === Number(res.round)) {
          state.resultProcessing = null;
        }
        playRoundAudioSequence(res.round);
      });
    },
    setMessages(state, list) {
      const normalized = (list || []).map((m) => ({
        id: m._id || m.id,
        message: m.message,
        senderId: m.senderId || m.sender_id || m.sender,
        senderName: m.senderName,
        timestamp: m.timestamp,
      }));
      // 최근이 아래로 쌓이도록 시간 오름차순 정렬
      normalized.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      state.messages = normalized;
    },
    setCurrentUser(state, user) {
      state.currentUser = user;
    },
    mountGame(state, gameRes) {
      // 초기 API 응답 도중 먼저 도착한 소켓 결과를 잃지 않도록 회차 기준 병합
      const normalized = [...state.gameResults, ...(gameRes || [])]
        .map((r) => ({
          ...r,
          parity: r.parity || (r.value % 2 === 0 ? '짝' : '홀'),
          side: r.value <= 4 ? '언더' : '오버',
        }))
        .filter(
          (result, index, list) =>
            list.findIndex((item) => Number(item.round) === Number(result.round)) === index,
        )
        .sort((a, b) => Number(b.round) - Number(a.round))
        .slice(0, 100);
      state.gameResults = normalized;
    },
  },
  actions: {},
  modules: {},
});
